import React from 'react'
import { createPortal } from 'react-dom'
import { AudioLines, X } from 'lucide-react'
import AudioAttachmentPlayer from 'components/answers/FileEmbed/AudioAttachmentPlayer'
import styles from './AiMediaPreviewModal.module.scss'

export type AiMediaPreviewKind = 'image' | 'video' | 'audio' | 'text'

export type AiMediaPreviewInput = {
    url?: string | null
    label?: string | null
    previewImageUrl?: string | null
    contentType?: string | null
    // Full text body for kind: 'text' (read-only prompt/lyrics viewers).
    text?: string | null
}

export type AiMediaPreviewTarget = {
    kind: AiMediaPreviewKind
    input: AiMediaPreviewInput
}

type AiMediaPreviewModalProps = {
    target: AiMediaPreviewTarget | null
    onClose: () => void
    footerMode?: 'select' | 'done'
    canRemove?: boolean
    canAdd?: boolean
    onRemove?: () => void
    onAdd?: () => void
    addLabel?: string
    doneLabel?: string
}

export default function AiMediaPreviewModal({
    target,
    onClose,
    footerMode = 'select',
    canRemove = false,
    canAdd = false,
    onRemove,
    onAdd,
    addLabel,
    doneLabel = 'Done',
}: AiMediaPreviewModalProps) {
    if (!target || typeof document === 'undefined') return null

    const label = target.input.label || `Selected ${target.kind}`
    const showSelectFooter = footerMode === 'select' && (canRemove || canAdd)
    const showDoneFooter = footerMode === 'done'
    const stopAudioEventPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }
    const preventAudioEventPropagation = (event: React.SyntheticEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }

    return createPortal(
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label={`${label} preview`}
            onMouseDown={event => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <button
                className={styles.closeButton}
                type="button"
                aria-label="Close preview"
                onClick={onClose}
            >
                <X size={22} />
            </button>
            <div className={styles.stage} onMouseDown={event => event.stopPropagation()}>
                {target.kind === 'image' && target.input.url && (
                    <img src={target.input.url} alt={label} />
                )}
                {target.kind === 'video' && target.input.url && (
                    <video
                        src={target.input.url}
                        controls
                        playsInline
                        poster={target.input.previewImageUrl || undefined}
                    />
                )}
                {target.kind === 'text' && !!target.input.text && (
                    <div className={styles.textPreview}>
                        <span className={styles.textLabel}>{label}</span>
                        <pre className={styles.textBody}>{target.input.text}</pre>
                    </div>
                )}
                {target.kind === 'audio' && target.input.url && (
                    <div className={styles.audioPreview}>
                        <AudioLines size={42} />
                        <span className={styles.audioLabel}>{label}</span>
                        <div className={styles.audioPlayer}>
                            <AudioAttachmentPlayer
                                src={target.input.url}
                                fileName={label}
                                audioSourceType={target.input.contentType || undefined}
                                theme="default"
                                isPaused={undefined}
                                persistPlayback={false}
                                resumePlaybackToken={undefined}
                                onDurationChange={undefined}
                                onProgressChange={undefined}
                                onPlaybackError={undefined}
                                onPlaybackStateChange={undefined}
                                onManualPlaybackChange={undefined}
                                matchingBsvAttachment={undefined}
                                renderBsvAttachmentStatus={undefined}
                                showDelete={undefined}
                                onDelete={undefined}
                                statusOverlayClassName={undefined}
                                statusOverlayWithDeleteClassName={undefined}
                                deleteClassName={undefined}
                                stopPropagationOnly={stopAudioEventPropagation}
                                preventPropagation={preventAudioEventPropagation}
                            />
                        </div>
                    </div>
                )}
            </div>
            {showSelectFooter && (
                <div className={styles.footer}>
                    <button className={styles.secondaryButton} type="button" onClick={onClose}>
                        Cancel
                    </button>
                    {canRemove ? (
                        <button className={styles.dangerButton} type="button" onClick={onRemove}>
                            Remove
                        </button>
                    ) : (
                        <button className={styles.primaryButton} type="button" onClick={onAdd}>
                            {addLabel || `Add ${target.kind}`}
                        </button>
                    )}
                </div>
            )}
            {showDoneFooter && (
                <div className={styles.footer}>
                    <button className={styles.primaryButton} type="button" onClick={onClose}>
                        {doneLabel}
                    </button>
                </div>
            )}
        </div>,
        document.body,
    )
}
